/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { GanttRowAxis } from '../Gantt.types';
import { getUnresolvedTimeAxesLayout } from '../../PRIVATE_TimeComponent/utils/timeAxisLayoutUtils';
import { bridgeValues as timeCompBridgeValues } from '../../PRIVATE_TimeComponent/themes/TimeComponentStyles.css';
import { xUnits } from '../../UNSAFE_Theme';

type RowAxisWidth<D1> = NonNullable<GanttRowAxis<D1>['width']>;
type RowAxisMaxWidth<D1> = NonNullable<GanttRowAxis<D1>['maxWidth']>;
type RowAxisLabel<D1> = NonNullable<GanttRowAxis<D1>['labelRenderer']>;

/**
 * Gets the row axis layout.
 * @param timeAxesLayout The time axes layout.
 * @param rowAxisProps The row axis props.
 * @returns
 */
const getWithRowAxisLayout = <D1>(
  timeAxesLayout: ReturnType<typeof getUnresolvedTimeAxesLayout>,
  rowAxisProps: {
    width: RowAxisWidth<D1>;
    maxWidth: RowAxisMaxWidth<D1>;
    label: RowAxisLabel<D1>;
  }
) => {
  const horiScrollbarHeight = `${xUnits(1)} + ${timeCompBridgeValues.horizontalScrollbarHeight}`;
  const generalLayout = {
    top: {
      rowAxisTopGap: timeAxesLayout.height,
      rowAxisBottomGap: `calc(${horiScrollbarHeight})`
    },
    bottom: {
      rowAxisTopGap: '0',
      rowAxisBottomGap: `calc(${timeAxesLayout.height} + ${horiScrollbarHeight})`
    }
  };

  const resolvedWidth = getResolvedLength(rowAxisProps.width);
  const resolvedMaxWidth = getResolvedLength(rowAxisProps.maxWidth);

  return {
    ...generalLayout[timeAxesLayout.position],
    resolvedRowAxisWidth: resolvedWidth,
    resolvedRowAxisMaxWidth: resolvedMaxWidth,
    label: rowAxisProps.label
  };
};

/**
 * Gets the parsed length given the values that the row axis accepts.
 * @param length The length values that the row axis accepts.
 * @returns The parsed length.
 */
const getResolvedLength = <D1>(length: RowAxisWidth<D1> | RowAxisMaxWidth<D1>) => {
  if (length.endsWith('px')) {
    return {
      value: Number(length.substring(0, length.length - 2)),
      suffix: 'px' as const
    };
  }
  if (length.endsWith('%')) {
    return {
      value: Number(length.substring(0, length.length - 1)),
      suffix: '%' as const
    };
  }
  // length is 'max-content' for width and 'none' for max-width
  return {
    value: Infinity,
    suffix: undefined
  };
};

export { getWithRowAxisLayout };
